import { fetchAdminSettings, patchAdminSettings, type IAdminSettings } from '@/api/admin';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import { CardContent } from '@/components/ui/card';
import SettingsWidgetCard from './settings-widget-card';
import { InputGroup, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { $updateFeatures } from '@/entities/features-entity';
import { useUnit } from 'effector-react';
import { CheckCircle2Icon, CircleAlertIcon, LayoutTemplateIcon } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';

const MIN_DEDUP_MS = 0;
const MAX_DEDUP_MS = 3_600_000;
const MIN_TTL_SECONDS = 0;
const MAX_TTL_SECONDS = 31_536_000;
const MIN_CHECK_INTERVAL_MS = 60_000;
const MAX_CHECK_INTERVAL_MS = 3_600_000;
const MIN_NS_TIMEOUT_DAYS = 1;
const MAX_NS_TIMEOUT_DAYS = 30;
const IPV4_REGEX = /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)$/;
const CLOUDFLARE_ACCOUNT_ID_REGEX = /^[a-f0-9]{32}$/i;

type LandingsInfrastructureMissing = NonNullable<IAdminSettings['landingsInfrastructureMissing']>[number];

const INFRASTRUCTURE_MISSING_LABELS: Record<LandingsInfrastructureMissing, string> = {
	cloudflare_token: 'Cloudflare API Token',
	cloudflare_account_id: 'Cloudflare Account ID',
	origin_ip: 'Origin IP',
};

const INFRASTRUCTURE_CHECKLIST: LandingsInfrastructureMissing[] = [
	'cloudflare_token',
	'cloudflare_account_id',
	'origin_ip',
];

type FormInfraInput = {
	serverMissing: LandingsInfrastructureMissing[];
	cfToken: string;
	cfTokenMasked: string | null;
	cfAccountId: string;
	originIp: string;
};

function getEffectiveInfrastructureMissing({
	serverMissing,
	cfToken,
	cfTokenMasked,
	cfAccountId,
	originIp,
}: FormInfraInput): LandingsInfrastructureMissing[] {
	let missing = [...serverMissing];

	if (missing.includes('cloudflare_account_id') && CLOUDFLARE_ACCOUNT_ID_REGEX.test(cfAccountId.trim())) {
		missing = missing.filter(key => key !== 'cloudflare_account_id');
	}

	if (missing.includes('cloudflare_token') && (cfTokenMasked || cfToken.trim().length > 0)) {
		missing = missing.filter(key => key !== 'cloudflare_token');
	}

	if (missing.includes('origin_ip') && IPV4_REGEX.test(originIp.trim())) {
		missing = missing.filter(key => key !== 'origin_ip');
	}

	return missing;
}

export default function LandingsSettingsWidget() {
	const updateFeatures = useUnit($updateFeatures);
	const [landingsEnabled, setLandingsEnabled] = useState(false);
	const [dedupInput, setDedupInput] = useState('');
	const [ttlInput, setTtlInput] = useState('');
	const [checkIntervalInput, setCheckIntervalInput] = useState('');
	const [nsTimeoutInput, setNsTimeoutInput] = useState('');
	const [cfToken, setCfToken] = useState('');
	const [cfTokenMasked, setCfTokenMasked] = useState<string | null>(null);
	const [cfAccountId, setCfAccountId] = useState('');
	const [originIp, setOriginIp] = useState('');
	const [landingsInfrastructureMissing, setLandingsInfrastructureMissing] = useState<LandingsInfrastructureMissing[]>(
		[],
	);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);

	function applySettings(settings: IAdminSettings) {
		setLandingsEnabled(settings.landingsEnabled ?? false);
		setDedupInput(String(settings.landingsConnectDedupMs ?? 60_000));
		setTtlInput(String(settings.landingsLinkTtlSeconds ?? 0));
		setCheckIntervalInput(String(settings.landingDomainCheckIntervalMs ?? 180_000));
		setNsTimeoutInput(String(settings.landingDomainNsTimeoutDays ?? 7));
		setCfToken('');
		setCfTokenMasked(settings.landingCloudflareApiToken ?? null);
		setCfAccountId(settings.landingCloudflareAccountId ?? '');
		setOriginIp(settings.landingOriginIp ?? '');
		setLandingsInfrastructureMissing(settings.landingsInfrastructureMissing ?? []);
	}

	useEffect(() => {
		let cancelled = false;

		async function load() {
			try {
				const settings = await fetchAdminSettings();
				if (!cancelled) applySettings(settings);
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		}

		void load();
		return () => {
			cancelled = true;
		};
	}, []);

	function getFormInfrastructureMissing(): LandingsInfrastructureMissing[] {
		return getEffectiveInfrastructureMissing({
			serverMissing: landingsInfrastructureMissing,
			cfToken,
			cfTokenMasked,
			cfAccountId,
			originIp,
		});
	}

	function handleLandingsEnabledChange(checked: boolean) {
		if (!checked) {
			setLandingsEnabled(false);
			return;
		}

		const effectiveMissing = getFormInfrastructureMissing();
		if (effectiveMissing.length === 0) {
			setLandingsEnabled(true);
			return;
		}

		toast.error('Сначала нажмите «Сохранить»');
	}

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();

		const landingsConnectDedupMs = Number(dedupInput);
		const landingsLinkTtlSeconds = Number(ttlInput);
		const landingDomainCheckIntervalMs = Number(checkIntervalInput);
		const landingDomainNsTimeoutDays = Number(nsTimeoutInput);

		if (
			!dedupInput.trim() ||
			!Number.isInteger(landingsConnectDedupMs) ||
			landingsConnectDedupMs < MIN_DEDUP_MS ||
			landingsConnectDedupMs > MAX_DEDUP_MS
		) {
			toast.error(`Connect dedup: от ${MIN_DEDUP_MS} до ${MAX_DEDUP_MS} мс`);
			return;
		}

		if (
			!ttlInput.trim() ||
			!Number.isInteger(landingsLinkTtlSeconds) ||
			landingsLinkTtlSeconds < MIN_TTL_SECONDS ||
			landingsLinkTtlSeconds > MAX_TTL_SECONDS
		) {
			toast.error(`TTL ссылки: от ${MIN_TTL_SECONDS} до ${MAX_TTL_SECONDS} сек (0 = без срока)`);
			return;
		}

		if (
			!checkIntervalInput.trim() ||
			!Number.isInteger(landingDomainCheckIntervalMs) ||
			landingDomainCheckIntervalMs < MIN_CHECK_INTERVAL_MS ||
			landingDomainCheckIntervalMs > MAX_CHECK_INTERVAL_MS
		) {
			toast.error(`Интервал проверки DNS: от ${MIN_CHECK_INTERVAL_MS} до ${MAX_CHECK_INTERVAL_MS} мс`);
			return;
		}

		if (
			!nsTimeoutInput.trim() ||
			!Number.isInteger(landingDomainNsTimeoutDays) ||
			landingDomainNsTimeoutDays < MIN_NS_TIMEOUT_DAYS ||
			landingDomainNsTimeoutDays > MAX_NS_TIMEOUT_DAYS
		) {
			toast.error(`Таймаут ожидания NS: от ${MIN_NS_TIMEOUT_DAYS} до ${MAX_NS_TIMEOUT_DAYS} дней`);
			return;
		}

		const trimmedOriginIp = originIp.trim();
		if (trimmedOriginIp && !IPV4_REGEX.test(trimmedOriginIp)) {
			toast.error('Origin IP: укажите корректный IPv4-адрес');
			return;
		}

		const trimmedAccountId = cfAccountId.trim();
		if (trimmedAccountId && !CLOUDFLARE_ACCOUNT_ID_REGEX.test(trimmedAccountId)) {
			toast.error('Cloudflare Account ID: укажите 32 hex-символа');
			return;
		}

		const patch: Partial<IAdminSettings> = {
			landingsEnabled,
			landingsConnectDedupMs,
			landingsLinkTtlSeconds,
			landingDomainCheckIntervalMs,
			landingDomainNsTimeoutDays,
			landingOriginIp: trimmedOriginIp || null,
			landingCloudflareAccountId: trimmedAccountId || null,
		};

		if (cfToken.trim()) {
			patch.landingCloudflareApiToken = cfToken.trim();
		}

		setIsSaving(true);
		try {
			const settings = await patchAdminSettings(patch);
			applySettings(settings);
			updateFeatures({ landingsEnabled: settings.landingsEnabled });
			toast.success('Настройки лендингов сохранены');
		} finally {
			setIsSaving(false);
		}
	}

	return (
		<SettingsWidgetCard
			id='landings'
			icon={LayoutTemplateIcon}
			title='Лендинги'
			description='Модуль веб-лендингов, Cloudflare и edge'
			titleClassName='text-chart-4'
			iconWrapperClassName='bg-chart-4/15'
		>
			<LoadingWrapper show={isSaving}>
				<CardContent className='px-4 py-4'>
					<form className='flex flex-col gap-4' onSubmit={handleSubmit}>
						<div className='rounded-lg border border-border bg-muted/40 px-3 py-3'>
							<p className='text-sm font-medium mb-2'>Готовность Cloudflare</p>
							<ul className='flex flex-col gap-1.5'>
								{INFRASTRUCTURE_CHECKLIST.map(key => {
									const isMissing = getFormInfrastructureMissing().includes(key);
									return (
										<li key={key} className='flex items-start gap-2 text-sm'>
											{isMissing ? (
												<CircleAlertIcon className='mt-0.5 size-4 shrink-0 text-destructive' />
											) : (
												<CheckCircle2Icon className='mt-0.5 size-4 shrink-0 text-chart-4' />
											)}
											<span className={isMissing ? 'text-muted-foreground' : undefined}>
												{INFRASTRUCTURE_MISSING_LABELS[key]}
											</span>
										</li>
									);
								})}
							</ul>
							<p className='text-xs text-muted-foreground mt-2'>
								Отдельный CF-аккаунт от device domains. Nginx vhost — через LANDING_NGINX_SITES_DIR в env
								landing-сервиса.
							</p>
						</div>

						<div className='flex items-center justify-between gap-3'>
							<div className='flex flex-col gap-0.5'>
								<Label htmlFor='landings-enabled'>Модуль лендингов</Label>
								<span className='text-xs text-muted-foreground'>Разрешить пользователям управлять лендингами</span>
							</div>
							<Switch
								id='landings-enabled'
								checked={landingsEnabled}
								onCheckedChange={handleLandingsEnabledChange}
								disabled={isLoading || getFormInfrastructureMissing().length > 0}
							/>
						</div>

						<Separator />

						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='landing-cf-api-token'>Cloudflare API Token</Label>
							<span className='text-xs text-muted-foreground'>
								{cfTokenMasked
									? `Токен задан (${cfTokenMasked}). Оставьте поле пустым, чтобы не менять.`
									: 'Токен не задан. Введите и сохраните.'}
							</span>
							<span className='text-xs text-muted-foreground'>
								Права: Account Settings Read, Zone Edit, DNS Edit, SSL and Certificates Edit (All zones).
							</span>
							<InputGroup>
								<InputGroupInput
									id='landing-cf-api-token'
									type='password'
									autoComplete='off'
									placeholder='••••••••'
									value={cfToken}
									onChange={e => setCfToken(e.target.value)}
									disabled={isLoading}
								/>
							</InputGroup>
						</div>

						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='landing-cf-account-id'>Cloudflare Account ID</Label>
							<span className='text-xs text-muted-foreground'>
								32 hex-символа из URL дашборда или Workers &amp; Pages → Account ID
							</span>
							<InputGroup>
								<InputGroupInput
									id='landing-cf-account-id'
									autoComplete='off'
									placeholder='758410f01a395281ae896d72887473e5'
									value={cfAccountId}
									onChange={e => setCfAccountId(e.target.value)}
									disabled={isLoading}
								/>
							</InputGroup>
						</div>

						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='landing-origin-ip'>Origin IP</Label>
							<span className='text-xs text-muted-foreground'>IP сервера для A-записи в Cloudflare</span>
							<InputGroup>
								<InputGroupInput
									id='landing-origin-ip'
									placeholder='203.0.113.10'
									value={originIp}
									onChange={e => setOriginIp(e.target.value)}
									disabled={isLoading}
								/>
							</InputGroup>
						</div>

						<Separator />

						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='landings-dedup-ms'>Connect dedup (мс)</Label>
							<span className='text-xs text-muted-foreground'>Интервал дедупликации connect-событий на edge</span>
							<InputGroup>
								<InputGroupInput
									id='landings-dedup-ms'
									type='number'
									inputMode='numeric'
									min={MIN_DEDUP_MS}
									max={MAX_DEDUP_MS}
									value={dedupInput}
									onChange={e => setDedupInput(e.target.value)}
									disabled={isLoading}
								/>
							</InputGroup>
						</div>

						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='landings-link-ttl'>TTL ссылки (сек)</Label>
							<span className='text-xs text-muted-foreground'>0 — ссылка без срока действия</span>
							<InputGroup>
								<InputGroupInput
									id='landings-link-ttl'
									type='number'
									inputMode='numeric'
									min={MIN_TTL_SECONDS}
									max={MAX_TTL_SECONDS}
									value={ttlInput}
									onChange={e => setTtlInput(e.target.value)}
									disabled={isLoading}
								/>
							</InputGroup>
						</div>

						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='landing-check-interval'>Интервал проверки DNS (мс)</Label>
							<span className='text-xs text-muted-foreground'>Частота фоновой проверки NS и активации доменов</span>
							<InputGroup>
								<InputGroupInput
									id='landing-check-interval'
									type='number'
									inputMode='numeric'
									min={MIN_CHECK_INTERVAL_MS}
									max={MAX_CHECK_INTERVAL_MS}
									value={checkIntervalInput}
									onChange={e => setCheckIntervalInput(e.target.value)}
									disabled={isLoading}
								/>
							</InputGroup>
						</div>

						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='landing-ns-timeout'>Таймаут ожидания NS (дней)</Label>
							<span className='text-xs text-muted-foreground'>После этого срока домен в waiting_dns переводится в error</span>
							<InputGroup>
								<InputGroupInput
									id='landing-ns-timeout'
									type='number'
									inputMode='numeric'
									min={MIN_NS_TIMEOUT_DAYS}
									max={MAX_NS_TIMEOUT_DAYS}
									value={nsTimeoutInput}
									onChange={e => setNsTimeoutInput(e.target.value)}
									disabled={isLoading}
								/>
							</InputGroup>
						</div>

						<Button type='submit' className='self-start' disabled={isLoading || isSaving}>
							Сохранить
						</Button>
					</form>
				</CardContent>
			</LoadingWrapper>
		</SettingsWidgetCard>
	);
}
